import type { GroupLayer, Layer, TemplateDoc } from './template';

/**
 * Column or row. `auto` follows the canvas shape (width ≥ autoRowRatio × height → row): the same
 * rule for every stack regardless of how the layout sized it, so the result is predictable.
 */
export function stackDirection(
  group: GroupLayer,
  canvas: { width: number; height: number },
): 'vertical' | 'horizontal' | 'free' {
  if (group.direction !== 'auto') return group.direction;
  return canvas.width >= canvas.height * (group.autoRowRatio ?? 1) ? 'horizontal' : 'vertical';
}

/** Valid stack parent of a layer (ignores dangling ids). */
export function parentOf(doc: TemplateDoc, layer: Layer): GroupLayer | undefined {
  if (!layer.parentId) return undefined;
  const p = doc.layers.find((l) => l.id === layer.parentId);
  return p?.type === 'group' && p.id !== layer.id ? p : undefined;
}

/** Direct children of a stack, in document order. */
export function childrenOf(doc: TemplateDoc, groupId: string): Layer[] {
  return doc.layers.filter((l) => l.parentId === groupId && l.id !== groupId);
}

/** Ids of every layer inside the given stacks (any depth). */
export function descendantIds(doc: TemplateDoc, ids: Iterable<string>): Set<string> {
  const out = new Set<string>();
  const queue = [...ids];
  while (queue.length) {
    const id = queue.pop()!;
    for (const l of doc.layers) {
      if (l.parentId === id && !out.has(l.id)) {
        out.add(l.id);
        queue.push(l.id);
      }
    }
  }
  return out;
}

/** Whether the layer sits (at any depth) inside a stack whose direction is `auto`. */
export function inAutoStack(doc: TemplateDoc, layer: Layer): boolean {
  const seen = new Set<string>();
  for (let p = parentOf(doc, layer); p && !seen.has(p.id); p = parentOf(doc, p)) {
    seen.add(p.id);
    if (p.direction === 'auto') return true;
  }
  return false;
}

/**
 * Laid out by its stack (a column or a row), so its own position is not used. A layer of a
 * `free` stack keeps its position, relative to the stack's top-left corner.
 */
export function inFlowStack(doc: TemplateDoc, layer: Layer): boolean {
  const p = parentOf(doc, layer);
  return Boolean(p && p.direction !== 'free');
}

/** The `free` stack the layer is placed in (its x and y count from that stack's corner). */
export function freeParentOf(doc: TemplateDoc, layer: Layer): GroupLayer | undefined {
  const p = parentOf(doc, layer);
  return p?.direction === 'free' ? p : undefined;
}
